import { isNil, isPlainObject, lowerCase, upperFirst } from 'lodash';

import type { AiAction } from '~/components/AiChat/types';
import { isDynamicClaimActionTaskData } from '~/components/AiChat/types';
import type { ExposureModel } from '~/components/types/exposure-types';

import { ACTION_NEEDS_ATTENTION } from '../constants';
import type { ActionState } from '../types';

/**
 * Keys the card renders elsewhere, or plumbing no handler reads. `exposure_id` is the exposure,
 * which the card names under its title; `envelope` is the wrapper the payload is read out of.
 * `exposure_ids` is not here: it is shown, by name, through `resolveExposureList`.
 */
const HIDDEN_KEYS = new Set([
  'task_text',
  'explanation',
  'task_type',
  'task_id',
  'tool_key',
  'related_exposure_id',
  'supersedes_action_id',
  'additional_actions',
  'done_action',
  'envelope',
  'exposure_id',
]);

// Money is recognised by the field's name, which covers every tool payload today (`amount`,
// `reserve_amount`, `reserve_indemnity`, `reserve_expense`, the legacy `payment_amount`).
// Anchored, so a `reserve_id` or an `amount_count` stays a number. A tool that names a money
// field differently needs its payload schema's money fields served to the client instead.
const MONEY_KEY = /(^|_)amount$|^reserve_(amount|indemnity|expense)$/;

/** The payload key `create_inspection` names its exposures by. */
export const EXPOSURE_LIST_KEY = 'exposure_ids';

const DOCUMENT_TEMPLATE_TOOL_KEY = 'create_document_from_template';

/**
 * The keys that tool names its template by. Given an id, the action reads neither of the others, so
 * they may be blank or name another template: the card shows the one template in a row of its own.
 * Without one, the action resolves by type and name together, so both stay rows of their own.
 */
const TEMPLATE_NAMING_KEYS = new Set(['template_id', 'document_type', 'template_name']);

/** The row the card shows that template in, in place of the keys it is named by. */
export const TEMPLATE_KEY = 'template';

/** A nested object has no labels of its own to read by, so it is not a row. */
const isNested = (value: unknown): boolean =>
  isPlainObject(value) || (Array.isArray(value) && value.some((item) => isPlainObject(item)));

const documentTemplatePayload = (action: AiAction): Record<string, unknown> | undefined =>
  isDynamicClaimActionTaskData(action.task_data) && action.task_data.tool_key === DOCUMENT_TEMPLATE_TOOL_KEY
    ? action.task_data.envelope?.payload
    : undefined;

/** The template id in one row, where the payload first named the template, for the card to show by name. */
const withTemplateRow = (entries: [string, unknown][], templateId: unknown): [string, unknown][] => {
  const at = entries.findIndex(([key]) => TEMPLATE_NAMING_KEYS.has(key));
  const rows = entries.filter(([key]) => !TEMPLATE_NAMING_KEYS.has(key));
  rows.splice(at, 0, [TEMPLATE_KEY, templateId]);

  return rows;
};

/**
 * What the action writes to the claim, as `[key, value]` pairs in the order the payload holds them.
 *
 * A dynamic claim action — every conductor action — carries its fields in `envelope.payload`, which
 * is what `execute_action_tool` validates and writes; the legacy per-type actions carry theirs flat
 * on `task_data`. A template card that names its template by id has it as one `template` pair (`withTemplateRow`).
 */
export const payloadEntries = (action: AiAction): [string, unknown][] => {
  const fields = isDynamicClaimActionTaskData(action.task_data) ? action.task_data.envelope?.payload : action.task_data;
  const entries = Object.entries(fields ?? {}).filter(
    ([key, value]) => !HIDDEN_KEYS.has(key) && value !== undefined && !isNested(value)
  );

  const templateId = documentTemplatePayload(action)?.template_id;

  return isNil(templateId) ? entries : withTemplateRow(entries, templateId);
};

/** Whether the card names its template by id, the one naming that is shown by the name it resolves to. */
export const namesTemplateById = (action: AiAction): boolean => !isNil(documentTemplatePayload(action)?.template_id);

type ClaimExposure = Partial<
  Pick<ExposureModel, 'id' | 'claim_internal_id' | 'claim_id_display_with_exposure_suffix' | 'label_text'>
>;

/** Every form an exposure may be named by, as `exposure_identifier._identifiers` accepts them. */
const exposureIdentifiers = (exposure: ClaimExposure): string[] =>
  [exposure.claim_internal_id, exposure.claim_id_display_with_exposure_suffix, exposure.label_text, exposure.id]
    .filter((form) => !isNil(form))
    .map((form) => String(form).toLowerCase());

/**
 * `exposure_ids` as the claim's exposure names. The agent writes a comma-separated list, naming
 * each exposure by any form `resolve_exposure` accepts, so each is matched the same way. One the
 * claim does not carry is shown as written rather than dropped.
 */
export const resolveExposureList = (value: string, exposures: ClaimExposure[] | undefined): string[] =>
  value
    .split(',')
    .map((identifier) => identifier.trim())
    .filter(Boolean)
    .map((identifier) => {
      const wanted = identifier.toLowerCase();
      const exposure = exposures?.find((candidate) => exposureIdentifiers(candidate).includes(wanted));

      return exposure?.label_text || identifier;
    });

/** `reserve_amount` → `Reserve amount`. The payload's keys are the only labels there are. */
export const payloadLabel = (key: string): string =>
  key === EXPOSURE_LIST_KEY ? 'Exposures' : upperFirst(lowerCase(key));

/** One value as it reads on the card. An em dash for nothing, so an absent value is still a row. */
export const payloadValue = (key: string, value: unknown, formatMoney: (amount: number) => string): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.map(String).join(' · ') : '—';
  if (typeof value === 'number' && MONEY_KEY.test(key)) return formatMoney(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  return String(value);
};

/**
 * The payload's heading, which has to be true of the state: a rejected action wrote nothing, so
 * "What was written" on it would put on the record a change that never happened. A superseded
 * version of the action was only ever proposed, whatever became of the action since.
 */
export const payloadHeading = (state: ActionState, isSuperseded = false): string => {
  if (isSuperseded) return 'What was proposed';
  if (ACTION_NEEDS_ATTENTION[state]) return 'What will be written to the claim';
  if (state === 'executed') return 'What was written to the claim';

  return 'What was proposed';
};
